import React, { useState, useEffect, useRef } from "react";
import { TiTick } from "react-icons/ti";

// 👉 INSTRUCTIONS: 
// Create a folder named "faces" inside your React "public" folder (public/faces/).
// Save 5 pictures of a person looking in the correct directions and name them exactly as below:
const RealFaceImages = {
  frontal: "/faces/front.jpg",
  left: "/faces/left.jpg",
  right: "/faces/right.jpg",
  up: "/faces/up.jpg",
  down: "/faces/down.jpg"
};

const POSE_STEPS = [
  { 
    id: "frontal", 
    label: "Look Straight", 
    visual: <img src={RealFaceImages.frontal} alt="Front" className="pose-real-image" /> 
  },
  { 
    id: "left", 
    label: "Turn Left", 
    visual: <img src={RealFaceImages.left} alt="Left" className="pose-real-image" /> 
  },
  { 
    id: "right", 
    label: "Turn Right", 
    visual: <img src={RealFaceImages.right} alt="Right" className="pose-real-image" /> 
  },
  { 
    id: "up", 
    label: "Look Up", 
    visual: <img src={RealFaceImages.up} alt="Up" className="pose-real-image" /> 
  },
  { 
    id: "down", 
    label: "Look Down", 
    visual: <img src={RealFaceImages.down} alt="Down" className="pose-real-image" /> 
  }
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const AutoFaceIdCamera = ({ onImageCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStepIdx, setCurrentStepIdx] = useState(-1);
  const [matchStatus, setMatchStatus] = useState("waiting"); 

  useEffect(() => {
    async function startVideo() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480, facingMode: "user" },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setCameraActive(true);
        }
      } catch (err) {
        console.error("Camera access error:", err);
      }
    }
    startVideo();

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startSequence = async () => {
    if (!cameraActive) return;
    setIsRunning(true);
    
    for (let i = 0; i < POSE_STEPS.length; i++) {
      setCurrentStepIdx(i);
      setMatchStatus("matching");
      
      await sleep(500); 
      
      setMatchStatus("matched");
      await snapFrame(POSE_STEPS[i]);
      
      await sleep(300); 
    }

    setIsRunning(false);
    setMatchStatus("waiting");
    setCurrentStepIdx(5); 
  };

  const captureSingleAngle = async (step, index) => {
    if (!cameraActive || isRunning) return;
    setCurrentStepIdx(index);
    setMatchStatus("matched");
    await snapFrame(step);
    await sleep(400); 
    setMatchStatus("waiting");
    setCurrentStepIdx(-1);
  };

  const snapFrame = (step) => {
    return new Promise((resolve) => {
      if (!videoRef.current || !canvasRef.current) return resolve();

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `face_${step.id}.png`, { type: "image/png" });
          onImageCapture(file);
        }
        resolve();
      }, "image/png");
    });
  };

  const currentStep = POSE_STEPS[currentStepIdx];

  return (
    <div className="faceid-container">
      
      <div className={`video-wrapper ${matchStatus === "matched" ? "flash-success" : ""}`}>
        <video ref={videoRef} autoPlay playsInline muted className="faceid-video-feed" />
        <div className={`faceid-ring ${currentStepIdx >= 5 ? "complete" : ""}`} />
      </div>

      <div className="visual-guide-area">
        {!isRunning && currentStepIdx < 5 && (
          <button className="start-sequence-btn" onClick={startSequence} disabled={!cameraActive}>
            Start Fast Auto-Capture
          </button>
        )}

        {isRunning && currentStep && (
          <div className={`guide-box ${matchStatus}`}>
            <h3 className="guide-text">
              {matchStatus === "matched" ? "Captured!" : currentStep.label}
            </h3>
          </div>
        )}

        {!isRunning && currentStepIdx >= 5 && (
          <div className="guide-box complete">
             <TiTick size={40} color="#00bcd4" />
             <h3 className="guide-text">Sequence Complete</h3>
          </div>
        )}
      </div>

      <div style={{ marginTop: "20px", textAlign: "center", width: "100%" }}>
        <p style={{ color: "#555", fontSize: "13px", marginBottom: "12px", fontWeight: "600" }}>
          Deleted a photo? Click a photo below to instantly retake it.
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          {POSE_STEPS.map((step, idx) => (
            <button
              key={step.id}
              onClick={() => captureSingleAngle(step, idx)}
              disabled={isRunning}
              title={`Instantly retake ${step.label}`}
              style={{
                background: currentStepIdx === idx ? "#e0f7fa" : "#f8f9fa",
                border: currentStepIdx === idx ? "2px solid #00bcd4" : "1px solid #ddd",
                borderRadius: "8px",
                padding: "8px",
                cursor: isRunning ? "not-allowed" : "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: "#333",
                transition: "all 0.2s",
                width: "75px"
              }}
            >
              {step.visual}
              <span style={{ fontSize: "12px", marginTop: "6px", fontWeight: "500", textTransform: "capitalize" }}>{step.id}</span>
            </button>
          ))}
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default AutoFaceIdCamera;
